import { FormInstance, Spin, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  APIError,
  AssetAllocationRate,
  FormFieldConfig,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import {
  GET_ASSET_ALLOCATION_RATE,
  UPDATE_ASSET_ALLOCATION_RATE,
} from "../gql/economicIndicators";

interface RouteParams {
  id: string;
}

interface State {
  id: string;
  isLoading: boolean;
  assetAllocationRate?: AssetAllocationRate;
}

export default class EditAssetAllocationRate extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      isLoading: true,
    };
    this.reference = React.createRef<FormInstance>();
    this.getAssetAllocationRate = this.getAssetAllocationRate.bind(this);
    this.updateAssetAllocationRate = this.updateAssetAllocationRate.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getAssetAllocationRate();
    });
  }

  getAssetAllocationRate = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ASSET_ALLOCATION_RATE,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const assetAllocationRate = data.data?.assetAllocationRate;
            this.setState({
              assetAllocationRate: assetAllocationRate,
            });
          } else {
            message.error("Failed to get asset allocation rate data");
          }
        });
      },
    );
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  redirectToViewAssetAllocationItemPage(): void {
    this.props.history.push(`/asset-allocation-rates/${this.state.id}`);
  }

  async updateAssetAllocationRate(): Promise<void> {
    const { id } = this.state;
    interface Response {
      data?: {
        updateAssetAllocationRate: {
          assetAllocationRate: AssetAllocationRate;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_ASSET_ALLOCATION_RATE,
        variables: {
          id,
          ...data,
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateAssetAllocationRate",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Asset allocation rate updated sucessfully");
        this.redirectToViewAssetAllocationItemPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update asset allocation rate - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  render(): JSX.Element | null {
    const { isLoading, assetAllocationRate } = this.state;
    const fields: FormFieldConfig[] = [
      {
        id: "name",
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        initialValue: assetAllocationRate?.name,
        placeholder: "Name",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "updateAssetAllocationRate",
        name: "updateAssetAllocationRateButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateAssetAllocationRate,
      },
    ];

    return (
      <Card header="UPDATE ASSET ALLOCATION RATE">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
